import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from './ThemeContext';
import { Star, Quote, Award, CheckCircle, Calendar, Clock, Globe, Plus, X, Pause } from 'lucide-react';

// SWIPER IMPORTS
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

export default function Testimonials({ isVisible = true }) {
  const { isDarkMode } = useTheme();
  const [liveTime, setLiveTime] = useState(new Date());
  const [expandedId, setExpandedId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const swiperRef = useRef(null);
  
  // ========= PERSISTENCE & RANDOM GROWTH =========
  const [counts, setCounts] = useState({ students: 1450, success: 98, rating: 4.9, countries: 30 });

  useEffect(() => {
    const savedData = localStorage.getItem('vidyazon_stats');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      if (new Date().getTime() - parsed.timestamp < 7 * 24 * 60 * 60 * 1000) {
        setCounts(prev => ({ ...prev, students: parsed.students }));
      }
    }

    const timer = setInterval(() => setLiveTime(new Date()), 1000);
    const growthInterval = setInterval(() => {
      setCounts(prev => {
        const newCount = prev.students + Math.floor(Math.random() * 2) + 1;
        localStorage.setItem('vidyazon_stats', JSON.stringify({ students: newCount, timestamp: new Date().getTime() }));
        return { ...prev, students: newCount };
      });
    }, 6000);

    return () => { clearInterval(timer); clearInterval(growthInterval); };
  }, []);

  // INSTANT PAUSE HANDLERS
  const handleMouseEnter = () => {
    setIsPaused(true);
    if (swiperRef.current) {
      swiperRef.current.autoplay.stop();
      // This forces the CSS transition to freeze immediately
      swiperRef.current.wrapperEl.style.transitionDuration = '0ms';
    }
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    if (swiperRef.current) {
      swiperRef.current.autoplay.start();
      // Restores the smooth linear speed
      swiperRef.current.wrapperEl.style.transitionDuration = '6000ms';
    }
  };

  const testimonials = [
    { id: 1, text: "This platform made finding my dream university so easy! The AI matching system understood exactly what I was looking for, from budget to course preferences, making the transition to USA seamless and stress-free.", name: "Sarah Johnson", role: "Stanford University", gradient: "from-blue-500 to-cyan-500" },
    { id: 2, text: "The counselors guided me through every step and helped me secure a $50,000 scholarship! I couldn't have navigated the complex Oxford application process without their constant mentorship.", name: "Michael Chen", role: "Oxford University", gradient: "from-purple-500 to-pink-500" },
    { id: 3, text: "I found the perfect university thanks to the comparison tool! It allowed me to see exactly which medical schools in Canada accepted my scores and offered the best residency pathways.", name: "Priya Sharma", role: "University of Toronto", gradient: "from-green-500 to-emerald-500" },
    { id: 4, text: "Visa guidance and application tracking was seamless. I was worried about the Australian GTE requirements, but the team provided a checklist that made everything clear and successful.", name: "David Martinez", role: "University of Melbourne", gradient: "from-orange-500 to-red-500" },
    { id: 5, text: "I discovered universities that were perfect for my profile! The portfolio review for my Arts & Design application to NYU was the deciding factor in my successful admission.", name: "Emma Thompson", role: "NYU", gradient: "from-indigo-500 to-blue-500" },
    { id: 6, text: "Incredible support throughout the visa process. They handled all my documentation for Germany and made sure I was prepared for my embassy interview.", name: "Arjun Mehta", role: "TU Munich", gradient: "from-teal-500 to-blue-600" }
  ];

  return (
    <section className={`relative py-20 px-4 overflow-hidden transition-colors duration-500 ${isDarkMode ? 'bg-[#030712]' : 'bg-gray-50'}`}>
      
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className={`absolute top-0 right-1/4 w-[500px] h-[500px] blur-[150px] rounded-full ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`} />
        <div className={`absolute bottom-0 left-1/4 w-[500px] h-[500px] blur-[150px] rounded-full ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-100'}`} />
      </div>

      <div className="max-w-[1600px] mx-auto relative z-10">
        
        {/* ========= LIVE HEADER ========= */}
        <div className="flex flex-col items-center mb-12 text-center">
          <div className={`inline-flex items-center gap-4 px-6 py-2 rounded-full border shadow-xl backdrop-blur-xl mb-8 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2 text-red-500 font-black text-[10px] uppercase tracking-widest">
              <span className={`w-2 h-2 rounded-full ${isPaused ? 'bg-orange-500' : 'bg-red-500 animate-ping'}`} />
              {isPaused ? 'Ticker Paused' : 'Live Pulse'}
            </div>
            <div className={`text-xs font-bold font-mono ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {liveTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
            </div>
          </div>
          <h2 className={`text-4xl sm:text-6xl font-black mb-4 tracking-tighter ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Vidyazon <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Hall of Success</span>
          </h2>
        </div>

        {/* ========= CONTINUOUS SWIPER ========= */}
        <div 
          className="relative px-0"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Visual Pause Overlay */}
          {isPaused && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8 z-50 flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-black uppercase tracking-tighter shadow-lg animate-in slide-in-from-top-2">
              <Pause size={10} fill="currentColor" /> Interactive Mode
            </div>
          )}

          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            modules={[Autoplay, Pagination]}
            spaceBetween={24}
            slidesPerView={1}
            loop={true}
            speed={6000}
            autoplay={{
              delay: 0,
              disableOnInteraction: false,
            }}
            breakpoints={{
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
              1440: { slidesPerView: 4 }
            }}
            className="continuous-swiper pb-10"
          >
            {testimonials.map((t) => (
              <SwiperSlide key={t.id} className="h-full">
                <div className={`relative flex flex-col h-[380px] p-8 rounded-[2.5rem] border transition-all duration-300 group cursor-default ${
                  isDarkMode ? 'bg-gray-900/40 border-gray-800 hover:bg-gray-800/60' : 'bg-white border-gray-100 shadow-lg'
                }`}>
                  
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, j) => <Star key={j} size={12} className="fill-yellow-400 text-yellow-400" />)}
                    </div>
                    <Quote className={`${isDarkMode ? 'text-gray-800' : 'text-gray-100'} w-8 h-8`} />
                  </div>

                  <div className="flex-1 relative overflow-hidden">
                    <p className={`text-base font-medium leading-relaxed italic ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} line-clamp-5`}>
                      "{t.text}"
                    </p>
                    
                    {t.text.length > 120 && (
                      <div className={`absolute bottom-0 left-0 w-full flex justify-center pt-8 bg-gradient-to-t ${
                        isDarkMode ? 'from-gray-900/90 to-transparent' : 'from-white/90 to-transparent'
                      }`}>
                        <button 
                          onClick={() => setExpandedId(t)}
                          className="text-[10px] font-black text-blue-500 uppercase tracking-widest hover:scale-110 transition-transform"
                        >
                          Read Full Story
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex items-center gap-4 pt-6 border-t border-gray-500/10">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white font-black shadow-lg`}>
                      {t.name[0]}
                    </div>
                    <div className="min-w-0">
                      <h4 className={`font-bold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t.name}</h4>
                      <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest">{t.role}</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* ========= LIVE STATS ========= */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-6xl mx-auto">
          {[
            { label: "Students", val: counts.students.toLocaleString() + "+", icon: Award, color: "text-blue-500" },
            { label: "Success", val: counts.success + "%", icon: CheckCircle, color: "text-green-500" },
            { label: "Rating", val: counts.rating + "/5", icon: Star, color: "text-yellow-500" },
            { label: "Study Hubs", val: counts.countries + "+", icon: Globe, color: "text-purple-500" }
          ].map((s, i) => (
            <div key={i} className={`p-6 rounded-2xl border flex flex-col items-center transition-all ${isDarkMode ? 'bg-gray-900/60 border-gray-800' : 'bg-white border-gray-100 shadow-md'}`}>
              <div className={`text-2xl font-black mb-1 font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{s.val}</div>
              <div className="text-[9px] font-black text-gray-500 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ========= MODAL MODAL ========= */}
      {expandedId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-xl bg-black/40">
          <div className={`relative w-full max-w-lg p-10 rounded-[3rem] border shadow-2xl animate-in zoom-in-95 duration-200 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <button onClick={() => setExpandedId(null)} className="absolute top-8 right-8 p-2 rounded-full bg-gray-500/10 hover:bg-red-500/10 hover:text-red-500 transition-all">
              <X size={20} />
            </button>
            <Quote className="w-12 h-12 text-blue-500/20 mb-6" />
            <p className={`text-xl font-medium leading-relaxed italic mb-8 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>"{expandedId.text}"</p>
            <div className="flex items-center gap-4 pt-6 border-t border-gray-500/10">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${expandedId.gradient} flex items-center justify-center text-white font-bold text-2xl`}>{expandedId.name[0]}</div>
              <div>
                <h4 className={`font-black text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{expandedId.name}</h4>
                <p className="text-xs font-bold text-blue-500 uppercase tracking-widest">{expandedId.role}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        .continuous-swiper .swiper-wrapper {
          transition-timing-function: linear !important;
        }
        .swiper-pagination-bullet { background: #3b82f6 !important; }
        .swiper-pagination-bullet-active { width: 24px !important; border-radius: 4px !important; }
      `}</style>
    </section>
  );
}